import {
	type ColumnDef,
	DataTable,
	SearchInput,
	useUrlPagination,
} from "@monbits/ui";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import userProvider, {
	type PaginatedUsersResponse,
	type UserDetails,
} from "src/providers/user-provider";

const formatDate = (dateStr: string): string => {
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const columns: ColumnDef<UserDetails>[] = [
	{
		id: "name",
		header: "Name",
		cell: (row) => (
			<div className="flex items-center gap-2">
				{row.profileImageUrl ? (
					<img
						src={row.profileImageUrl}
						alt={row.name}
						className="h-8 w-8 rounded-full"
					/>
				) : (
					<div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-sm font-semibold">
						{row.name.charAt(0).toUpperCase()}
					</div>
				)}
				<span className="font-medium">{row.name}</span>
			</div>
		),
	},
	{
		id: "email",
		header: "Email",
		cell: (row) => row.email,
	},
	{
		id: "merchants",
		header: "Merchants",
		cell: (row) => row.merchantCount,
	},
	{
		id: "created",
		header: "Created",
		cell: (row) => formatDate(row.createdAt),
	},
];

export function UsersList() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const searchQuery = searchParams.get("search") || "";

	const [users, setUsers] = React.useState<UserDetails[]>([]);
	const [totalCount, setTotalCount] = React.useState(0);
	const [isLoading, setIsLoading] = React.useState(true);

	const { page, limit, offset, setPage, setLimit } = useUrlPagination({
		totalCount,
	});

	const loadUsers = React.useCallback(
		async (offset: number, limit: number, search: string) => {
			try {
				setIsLoading(true);
				const response: PaginatedUsersResponse = await userProvider.listUsers({
					limit,
					offset,
					search,
					includeDeleted: false,
				});

				setUsers(response.users);
				setTotalCount(response.totalCount);
			} catch (error) {
				console.error("Failed to load users:", error);
				toast.error("Failed to load users", {
					description: "Please try again later",
				});
			} finally {
				setIsLoading(false);
			}
		},
		[],
	);

	React.useEffect(() => {
		loadUsers(offset, limit, searchQuery);
	}, [offset, limit, searchQuery, loadUsers]);

	const handleUserClick = React.useCallback(
		(user: UserDetails) => {
			navigate(`/users/${user.uuid}?${searchParams.toString()}`);
		},
		[navigate, searchParams],
	);

	return (
		<div className="p-4 sm:p-6 space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold">User Management</h1>
					<p className="text-sm text-muted-foreground mt-1">
						{totalCount} total user{totalCount !== 1 ? "s" : ""}
					</p>
				</div>
			</div>

			<div className="flex gap-2">
				<SearchInput
					placeholder="Search by email or name..."
					paramName="search"
					className="flex-1"
				/>
			</div>

			<DataTable
				columns={columns}
				data={users}
				isLoading={isLoading}
				onRowClick={handleUserClick}
				getRowId={(row) => row.uuid}
				emptyMessage={searchQuery ? "No users found" : "No users yet"}
				emptyDescription={
					searchQuery
						? "Try adjusting your search criteria"
						: "Users will appear here once they register"
				}
				pagination={{
					page,
					pageSize: limit,
					totalCount,
					onPageChange: setPage,
					onPageSizeChange: setLimit,
				}}
			/>
		</div>
	);
}
